import { actionTypes as dataFetchActionTypes } from '@bufferapp/async-data-fetch';
import { actionTypes as queueActionTypes } from '~/queue/reducer';
import { actionTypes as orgActionTypes } from '~/organizations';
import cloneDeep from 'lodash.clonedeep';
import keyWrapper from '@bufferapp/keywrapper';
import { refreshSingleProfile } from '~/profiles/thunks/fetchSingleProfile';
import { filterProfilesByOrg } from './utils';

export const actionTypes = keyWrapper('PROFILE_SIDEBAR', {
  SELECT_PROFILE: 0,
  PROFILE_UNPAUSED: 0,
  PROFILE_PAUSED: 0,
  PUSHER_PROFILE_PAUSED_STATE: 0,
  CONNECT_SOCIAL_ACCOUNT: 0,
  HANDLE_SEARCH_PROFILE_CHANGE: 0,
  PROFILE_ROUTE_LOADED: 0,
  PROFILE_DROPPED: 0,
});

export const initialState = {
  profiles: [],
  profileList: [],
  selectedProfileId: '',
  organization: null,
  loading: false,
  loaded: false,
  selectedProfile: {},
  searchText: null,
  userId: null,
};

const moveProfileInArray = (arr, from, to) => {
  const clone = [...arr];

  // Generate the new array
  Array.prototype.splice.call(
    clone,
    to,
    0,
    Array.prototype.splice.call(clone, from, 1)[0]
  );
  return clone;
};

const handleProfileThreadsCanQueueChanged = (profiles, action) => {
  const { profileId, canQueueMoreThreads } = action;
  const profileIndexInArray = profiles.findIndex((p) => p.id === profileId);
  const clonedProfiles = cloneDeep(profiles);
  clonedProfiles[profileIndexInArray].canQueueMoreThreads = canQueueMoreThreads;
  return clonedProfiles;
};

const handleProfileDropped = (profiles, action) => {
  const { hoverIndex, dragIndex } = action;
  const reorderedProfiles = moveProfileInArray(profiles, dragIndex, hoverIndex);
  /* The reducer will return an object with 3 properties, each of them an array of profiles.
  For each profile reduced, we will need to spread the ACC object,
  changing only 1 property, i.e., adding the profile to only 1 array */
  const { enabledProfiles, lockedProfiles, blockedProfiles } =
    reorderedProfiles.reduce(
      (acc, cur) => {
        return {
          ...acc,
          enabledProfiles: [
            ...acc.enabledProfiles,
            { ...cur, disabled: false },
          ],
        };
      },
      { enabledProfiles: [], lockedProfiles: [], blockedProfiles: [] }
    );

  // Final list of profiles
  const sortedProfiles = [
    ...enabledProfiles,
    ...lockedProfiles,
    ...blockedProfiles,
  ];

  return sortedProfiles;
};

const profilesReducer = (state = [], action) => {
  switch (action.type) {
    case actionTypes.SELECT_PROFILE:
      return state.map((profile) => ({
        ...profile,
        open: profile.id === action.profileId,
      }));
    case queueActionTypes.POST_COUNT_UPDATED:
      return state.map((profile) => ({
        ...profile,
        pendingCount:
          profile.id === action.profileId
            ? action.counts.pending
            : profile.pendingCount,
        sentCount:
          profile.id === action.profileId
            ? action.counts.sent
            : profile.sentCount,
      }));
    case actionTypes.PROFILE_PAUSED:
    case actionTypes.PROFILE_UNPAUSED:
      return state.map((profile) => ({
        ...profile,
        paused:
          profile.id === action.profileId
            ? action.type === actionTypes.PROFILE_PAUSED
            : profile.paused,
      }));
    case actionTypes.PUSHER_PROFILE_PAUSED_STATE:
      return state.map((profile) => ({
        ...profile,
        paused:
          profile.id === action.profileId ? action.paused : profile.paused,
      }));
    case actionTypes.HANDLE_SEARCH_PROFILE_CHANGE:
      return state.map((profile) => ({
        ...profile,
        filtered:
          !profile.handle.match(new RegExp(action.value, 'gi')) &&
          !profile.service.match(new RegExp(action.value, 'gi')),
      }));
    default:
      return state;
  }
};

export const getEnabledProfiles = (profiles) =>
  profiles.filter((profile) => !profile.disabled);

export default (state = initialState, action) => {
  let searchText = null;
  switch (action.type) {
    case `profiles_${dataFetchActionTypes.FETCH_START}`:
      // Ignore analyze specific actions so as not to flash loading state
      if (action.args && action.args.forAnalyze) {
        return state;
      }
      return {
        ...state,
        loading: true,
      };

    case `profiles_${dataFetchActionTypes.FETCH_SUCCESS}`: {
      const profiles = action.result;
      const profileList = getEnabledProfiles(profiles);

      return {
        ...state,
        loading: false,
        loaded: true,
        profileList,
        profiles: filterProfilesByOrg(profileList, state.organization),
      };
    }
    case orgActionTypes.ORGANIZATION_SELECTED: {
      const selectedOrganization = action.selected;
      let { profiles } = state;

      if (profiles) {
        const { profileList } = state;
        profiles = filterProfilesByOrg(profileList, selectedOrganization);
      }

      return {
        ...state,
        organization: selectedOrganization,
        profiles,
      };
    }
    case actionTypes.SELECT_PROFILE: {
      return {
        ...state,
        selectedProfileId: action.profileId,
        profiles: profilesReducer(state.profiles, action),
        selectedProfile: action.profile,
      };
    }
    case actionTypes.HANDLE_SEARCH_PROFILE_CHANGE:
      searchText = action.value;

      return {
        ...state,
        profiles: profilesReducer(state.profiles, action),
        searchText,
      };
    case `singleProfile_${dataFetchActionTypes.FETCH_SUCCESS}`:
    case `${refreshSingleProfile.fulfilled}`: {
      let { selectedProfile, profiles } = state;
      const profileReceived = action.result || action.payload;
      const { organization } = state;
      const isInCurrentOrganization =
        organization.id === profileReceived.organizationId;

      if (selectedProfile.id === profileReceived.id) {
        selectedProfile = profileReceived;
      }

      if (profiles.some((p) => p.id === profileReceived.id)) {
        profiles = profiles.map((profile) => {
          if (profile.id === profileReceived.id) {
            return profileReceived;
          }
          return profile;
        });
      } else if (isInCurrentOrganization) {
        profiles = [...profiles, profileReceived];
      }

      return {
        ...state,
        profiles,
        selectedProfile,
      };
    }

    case actionTypes.PROFILE_PAUSED:
    case actionTypes.PROFILE_UNPAUSED:
    case actionTypes.PUSHER_PROFILE_PAUSED_STATE:
    case queueActionTypes.POST_COUNT_UPDATED: {
      return {
        ...state,
        profiles: profilesReducer(state.profiles, action),
      };
    }
    case `user_${dataFetchActionTypes.FETCH_SUCCESS}`: {
      return {
        ...state,
        userId: action.result.id,
      };
    }
    case actionTypes.PROFILE_DROPPED: {
      if (!action.commit) {
        return {
          ...state,
          profiles: handleProfileDropped(state.profiles, action),
        };
      }
      return state;
    }
    case queueActionTypes.PROFILE_CAN_QUEUE_THREADS_CHANGED: {
      return {
        ...state,
        profiles: handleProfileThreadsCanQueueChanged(state.profiles, action),
      };
    }
    default:
      return state;
  }
};

export const actions = {
  selectProfile: ({ profile }) => ({
    type: actionTypes.SELECT_PROFILE,
    profileId: profile ? profile.id : null,
    profile,
  }),
  onUnpauseClick: ({ profileId }) => ({
    type: actionTypes.PROFILE_UNPAUSED,
    profileId,
  }),
  onPauseClick: ({ profileId }) => ({
    type: actionTypes.PROFILE_PAUSED,
    profileId,
  }),
  handleConnectSocialAccount: () => ({
    type: actionTypes.CONNECT_SOCIAL_ACCOUNT,
  }),
  onDropProfile: ({ commit, dragIndex, hoverIndex, profileLimit }) => ({
    type: actionTypes.PROFILE_DROPPED,
    commit,
    dragIndex,
    hoverIndex,
    profileLimit,
  }),
  handleSearchProfileChange: ({ value }) => ({
    type: actionTypes.HANDLE_SEARCH_PROFILE_CHANGE,
    value,
  }),
  handleProfileRouteLoaded: ({ selectedProfile, tabId }) => ({
    type: actionTypes.PROFILE_ROUTE_LOADED,
    selectedProfile,
    tabId,
  }),
};
